
import { GoogleGenAI, GenerateContentResponse } from "@google/genai";
import { Language, TypingStats } from "../types";

/**
 * Generates typing practice content based on a topic and language.
 */
export const generateTypingContent = async (language: Language, topic: string = "general interest"): Promise<string> => {
  try {
    const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
    const response: GenerateContentResponse = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: `Generate a short typing practice paragraph (about 40-50 words) related to ${topic} in the ${language} language. Use only lowercase letters and basic punctuation. No markdown.`,
    });
    return response.text || "failed to generate content, please try again.";
  } catch (error) {
    console.error("Gemini Error:", error);
    return "error connecting to the intelligence layer.";
  }
};

/**
 * Provides a real famous quote in the EXACT specified language.
 */
export const generateQuote = async (language: Language, length: 'short' | 'medium' | 'long' = 'medium'): Promise<string> => {
  try {
    const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
    const lengthHint = length === 'short' ? 'under 10 words' : length === 'medium' ? '15-25 words' : 'over 40 words';
    
    // Промпт усилен для предотвращения смешивания языков
    const response: GenerateContentResponse = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: `Provide a famous real quote from a well-known historical figure in the ${language.toUpperCase()} language. 
      CRITICAL: The quote MUST be written in ${language}. 
      The quote should be approximately ${lengthHint} long. 
      Return ONLY the text of the quote. Do NOT include author, quotes, or formatting.`,
    });
    
    return response.text?.trim() || "Patience is a virtue.";
  } catch (error) {
    console.error("Gemini Quote Error:", error);
    return "The quick brown fox jumps over the lazy dog.";
  }
};

/**
 * Generates personalized typing insights.
 */
export const getTypingInsights = async (stats: TypingStats, language: Language): Promise<string> => {
  try {
    const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
    const response: GenerateContentResponse = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: `The user typed at ${stats.wpm} WPM with ${stats.accuracy}% accuracy in ${language}. Give a one-sentence motivation in ${language}.`,
    });
    return response.text || "Keep practicing!";
  } catch (error) {
    return "Keep going, you're doing great.";
  }
};
